import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Target,
  Plus,
  Search,
  Filter,
  Calendar,
  DollarSign,
  Play,
  Pause,
  Check,
  Copy,
  Trash2,
  MoreVertical,
  TrendingUp,
  Eye,
  Heart,
  MessageCircle,
  X,
} from 'lucide-react';
import { api, type SMCCCampaign as Campaign } from '../../../services/api';

interface CampaignFormData {
  name: string;
  description: string;
  goal_type: string;
  budget: string;
  currency: string;
  start_date: string;
  end_date: string;
  target_platforms: string[];
}

// Platform configuration
const PLATFORM_CONFIG: Record<string, { name: string; color: string }> = {
  instagram: { name: 'Instagram', color: '#E4405F' },
  tiktok: { name: 'TikTok', color: '#000000' },
  twitter: { name: 'X (Twitter)', color: '#1DA1F2' },
  facebook: { name: 'Facebook', color: '#1877F2' },
  pinterest: { name: 'Pinterest', color: '#BD081C' },
  youtube: { name: 'YouTube', color: '#FF0000' },
  linkedin: { name: 'LinkedIn', color: '#0A66C2' },
};

const GOAL_TYPES = [
  { value: 'awareness', label: 'Brand Awareness', icon: Eye },
  { value: 'engagement', label: 'Engagement', icon: Heart },
  { value: 'traffic', label: 'Traffic', icon: TrendingUp },
  { value: 'conversions', label: 'Conversions', icon: Target },
  { value: 'followers', label: 'Follower Growth', icon: MessageCircle },
  { value: 'sales', label: 'Sales', icon: DollarSign },
];

type CampaignStatus = 'draft' | 'scheduled' | 'active' | 'paused' | 'completed' | 'archived';


function StatusBadge({ status }: { status: CampaignStatus }) {
  const config: Record<CampaignStatus, { color: string; label: string }> = {
    draft: { color: 'text-gray-600 bg-gray-100', label: 'Draft' },
    scheduled: { color: 'text-blue-600 bg-blue-50', label: 'Scheduled' },
    active: { color: 'text-green-600 bg-green-50', label: 'Active' },
    paused: { color: 'text-amber-600 bg-amber-50', label: 'Paused' },
    completed: { color: 'text-purple-600 bg-purple-50', label: 'Completed' },
    archived: { color: 'text-gray-500 bg-gray-100', label: 'Archived' },
  };

  const { color, label } = config[status];

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function CampaignCard({
  campaign,
  onActivate,
  onPause,
  onComplete,
  onDuplicate,
  onDelete,
  onClick,
}: {
  campaign: Campaign;
  onActivate: () => void;
  onPause: () => void;
  onComplete: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onClick: () => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const goalType = GOAL_TYPES.find((g) => g.value === campaign.goal_type);
  const GoalIcon = goalType?.icon || Target;

  const budgetPercent = campaign.budget
    ? Math.min(Math.round((campaign.spent / campaign.budget) * 100), 100)
    : 0;

  const dateRange = campaign.start_date && campaign.end_date
    ? `${new Date(campaign.start_date).toLocaleDateString()} - ${new Date(campaign.end_date).toLocaleDateString()}`
    : campaign.start_date
    ? `Starts ${new Date(campaign.start_date).toLocaleDateString()}`
    : 'No dates set';

  return (
    <button
      type="button"
      className="w-full text-left bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={onClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary-50 text-primary-600">
            <GoalIcon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{campaign.name}</h3>
            {campaign.description && (
              <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{campaign.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={campaign.status} />
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                  }}
                  onKeyDown={(e) => e.key === 'Escape' && setShowMenu(false)}
                  role="button"
                  tabIndex={0}
                  aria-label="Close menu"
                />
                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1 min-w-[140px]">
                  {campaign.status === 'draft' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onActivate();
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Play className="w-4 h-4" /> Activate
                    </button>
                  )}
                  {campaign.status === 'active' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onPause();
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Pause className="w-4 h-4" /> Pause
                    </button>
                  )}
                  {campaign.status === 'paused' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onActivate();
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Play className="w-4 h-4" /> Resume
                    </button>
                  )}
                  {(campaign.status === 'active' || campaign.status === 'paused') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onComplete();
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Check className="w-4 h-4" /> Complete
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicate();
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Copy className="w-4 h-4" /> Duplicate
                  </button>
                  {campaign.status !== 'active' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" /> Delete
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Date range */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
        <Calendar className="w-4 h-4" />
        {dateRange}
      </div>

      {/* Platforms */}
      {campaign.target_platforms && campaign.target_platforms.length > 0 && (
        <div className="flex items-center gap-1 mb-3">
          {campaign.target_platforms.map((platform) => {
            const config = PLATFORM_CONFIG[platform];
            return (
              <div
                key={platform}
                className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center"
                style={{ backgroundColor: config?.color || '#gray' }}
                title={config?.name || platform}
              >
                <span className="text-white text-xs font-bold">
                  {(config?.name || platform).charAt(0)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Budget */}
      {campaign.budget && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-500">Budget</span>
            <span className="font-medium text-gray-900">
              {formatCurrency(campaign.spent, campaign.currency)} / {formatCurrency(campaign.budget, campaign.currency)}
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                budgetPercent >= 90 ? 'bg-red-500' : budgetPercent >= 70 ? 'bg-amber-500' : 'bg-primary-500'
              }`}
              style={{ width: `${budgetPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Goal type */}
      {goalType && (
        <div className="text-xs text-gray-400">
          Goal: {goalType.label}
        </div>
      )}
    </button>
  );
}

function CreateCampaignModal({
  isOpen,
  onClose,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: CampaignFormData) => void;
}) {
  const [formData, setFormData] = useState<CampaignFormData>({
    name: '',
    description: '',
    goal_type: '',
    budget: '',
    currency: 'USD',
    start_date: '',
    end_date: '',
    target_platforms: [],
  });

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate({
      ...formData,
      budget: formData.budget ? String(parseFloat(formData.budget)) : '',
    });
    setFormData({
      name: '',
      description: '',
      goal_type: '',
      budget: '',
      currency: 'USD',
      start_date: '',
      end_date: '',
      target_platforms: [],
    });
  };

  const togglePlatform = (platform: string) => {
    setFormData((prev) => ({
      ...prev,
      target_platforms: prev.target_platforms.includes(platform)
        ? prev.target_platforms.filter((p) => p !== platform)
        : [...prev.target_platforms, platform],
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Create Campaign</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label htmlFor="campaign-name" className="block text-sm font-medium text-gray-700 mb-1">
              Campaign Name *
            </label>
            <input
              id="campaign-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Summer Sale 2024"
              required
            />
          </div>

          <div>
            <label htmlFor="campaign-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="campaign-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={2}
              placeholder="Campaign description..."
            />
          </div>

          <div>
            <label htmlFor="campaign-goal-type" className="block text-sm font-medium text-gray-700 mb-1">
              Goal Type
            </label>
            <select
              id="campaign-goal-type"
              value={formData.goal_type}
              onChange={(e) => setFormData({ ...formData, goal_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select a goal</option>
              {GOAL_TYPES.map((goal) => (
                <option key={goal.value} value={goal.value}>
                  {goal.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="campaign-start-date" className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                id="campaign-start-date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="campaign-end-date" className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                id="campaign-end-date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="campaign-budget" className="block text-sm font-medium text-gray-700 mb-1">
                Budget
              </label>
              <input
                id="campaign-budget"
                type="number"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="1000"
                min="0"
              />
            </div>
            <div>
              <label htmlFor="campaign-currency" className="block text-sm font-medium text-gray-700 mb-1">
                Currency
              </label>
              <select
                id="campaign-currency"
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="SEK">SEK</option>
              </select>
            </div>
          </div>

          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2">
              Target Platforms
            </span>
            <div className="flex flex-wrap gap-2">
              {Object.entries(PLATFORM_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => togglePlatform(key)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    formData.target_platforms.includes(key)
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={
                    formData.target_platforms.includes(key)
                      ? { backgroundColor: config.color }
                      : undefined
                  }
                >
                  {config.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.name}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              Create Campaign
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SMCCCampaigns() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    goal_type: '',
    search: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Fetch campaigns
  const { data: campaignsData, isLoading } = useQuery({
    queryKey: ['smcc-campaigns', filters],
    queryFn: () => api.getSMCCCampaigns({
      status: filters.status || undefined,
      goal_type: filters.goal_type || undefined,
      search: filters.search || undefined,
    }),
  });

  // Create campaign mutation
  const createMutation = useMutation({
    mutationFn: (data: CampaignFormData) => api.createSMCCCampaign({ ...data, budget: data.budget ? parseFloat(data.budget) : undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smcc-campaigns'] });
      setShowCreateModal(false);
    },
  });

  // Action mutations
  const activateMutation = useMutation({
    mutationFn: (id: string) => api.activateSMCCCampaign(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['smcc-campaigns'] }),
  });

  const pauseMutation = useMutation({
    mutationFn: (id: string) => api.pauseSMCCCampaign(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['smcc-campaigns'] }),
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => api.completeSMCCCampaign(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['smcc-campaigns'] }),
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => api.duplicateSMCCCampaign(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['smcc-campaigns'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteSMCCCampaign(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['smcc-campaigns'] }),
  });

  const campaigns: Campaign[] = campaignsData?.campaigns || [];

  // Group campaigns by status
  const activeCampaigns = campaigns.filter((c) => c.status === 'active');
  const draftCampaigns = campaigns.filter((c) => c.status === 'draft' || c.status === 'scheduled');
  const completedCampaigns = campaigns.filter((c) => c.status === 'completed' || c.status === 'paused');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
          <p className="text-gray-500 mt-1">
            Organize your content into marketing campaigns
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Campaign
        </button>
      </div>

      {/* Search and filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`p-2 rounded-lg border ${
            showFilters ? 'border-primary-500 bg-primary-50 text-primary-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Filter className="w-4 h-4" />
        </button>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-4">
          <div>
            <label htmlFor="filter-status" className="text-xs text-gray-500 block mb-1">Status</label>
            <select
              id="filter-status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div>
            <label htmlFor="filter-goal-type" className="text-xs text-gray-500 block mb-1">Goal Type</label>
            <select
              id="filter-goal-type"
              value={filters.goal_type}
              onChange={(e) => setFilters({ ...filters, goal_type: e.target.value })}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">All</option>
              {GOAL_TYPES.map((goal) => (
                <option key={goal.value} value={goal.value}>
                  {goal.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setFilters({ status: '', goal_type: '', search: '' })}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Campaign sections */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-16">
          <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No campaigns yet</h3>
          <p className="text-gray-500 mb-4">Create your first campaign to organize your content</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Create Campaign
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Active campaigns */}
          {activeCampaigns.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                Active Campaigns ({activeCampaigns.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeCampaigns.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    onClick={() => navigate(`/admin/smcc/campaigns/${campaign.id}`)}
                    onActivate={() => activateMutation.mutate(campaign.id)}
                    onPause={() => pauseMutation.mutate(campaign.id)}
                    onComplete={() => completeMutation.mutate(campaign.id)}
                    onDuplicate={() => duplicateMutation.mutate(campaign.id)}
                    onDelete={() => {
                      if (confirm('Are you sure you want to delete this campaign?')) {
                        deleteMutation.mutate(campaign.id);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Draft/Scheduled campaigns */}
          {draftCampaigns.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-gray-400" />
                Draft & Scheduled ({draftCampaigns.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {draftCampaigns.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    onClick={() => navigate(`/admin/smcc/campaigns/${campaign.id}`)}
                    onActivate={() => activateMutation.mutate(campaign.id)}
                    onPause={() => pauseMutation.mutate(campaign.id)}
                    onComplete={() => completeMutation.mutate(campaign.id)}
                    onDuplicate={() => duplicateMutation.mutate(campaign.id)}
                    onDelete={() => {
                      if (confirm('Are you sure you want to delete this campaign?')) {
                        deleteMutation.mutate(campaign.id);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Completed/Paused campaigns */}
          {completedCampaigns.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-500" />
                Completed & Paused ({completedCampaigns.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {completedCampaigns.map((campaign) => (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    onClick={() => navigate(`/admin/smcc/campaigns/${campaign.id}`)}
                    onActivate={() => activateMutation.mutate(campaign.id)}
                    onPause={() => pauseMutation.mutate(campaign.id)}
                    onComplete={() => completeMutation.mutate(campaign.id)}
                    onDuplicate={() => duplicateMutation.mutate(campaign.id)}
                    onDelete={() => {
                      if (confirm('Are you sure you want to delete this campaign?')) {
                        deleteMutation.mutate(campaign.id);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create campaign modal */}
      <CreateCampaignModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={(data) => createMutation.mutate(data)}
      />
    </div>
  );
}
